import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { filter as _filter, get as _get, has as _has } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { PolicySelectors } from '@amfam/policy/data-access';
import {
  NamedInsured,
  ReplaceVehicleQuoteApiRequest,
  UnderwritingQuestionModel
} from '@amfam/policy/models';
import {
  fromVehicleActions,
  UnderwritingQuestion,
  vehicleQuery
} from '@amfam/policy/vehicle/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  ErrorObj,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import * as replaceVehicleActions from '../../../core/store/replace-vehicle/replace-vehicle.actions';
import { ReplaceVehicleService } from '../../../core/store/replace-vehicle/replace-vehicle.service';

@Component({
  selector: 'ds-underwriting-question-list',
  templateUrl: './underwriting-question-list.component.html',
  styleUrls: ['./underwriting-question-list.component.scss'],
  standalone: false
})
export class UnderwritingQuestionListComponent implements OnInit, AfterViewInit, OnDestroy {
  questions: UnderwritingQuestionModel[] = [
    {
      question: this.copyService.getCopy('policy.underWritingQuestionIsOwner')['key'],
      mappedQuestion: this.copyService.getCopy('policy.underWritingQuestionIsOwner')['value'],
      id: 1
    },
    {
      question: this.copyService.getCopy('policy.underWritingQuestionIsBusinessUse')['key'],
      mappedQuestion: this.copyService.getCopy('policy.underWritingQuestionIsBusinessUse')['value'],
      id: 2
    },
    {
      question: this.copyService.getCopy('policy.underWritingQuestionHasLoan'),
      id: 3
    }
  ];

  tpiQuestion: UnderwritingQuestionModel = {
    question: '',
    id: 4
  };

  showTPIQuestion = false;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();
  underWritingQuestionsForm: UntypedFormGroup;
  underWritingQuestionsCtrl: AbstractControl;
  private vehicleId: string;
  private tpi: NamedInsured;
  policyNumber: string;
  replaceVin: string;
  hasError: Observable<boolean>;
  errorList: Array<ErrorObj> = [];
  errorMessage: string;

  constructor(
    private store: Store<fromRoot.RootState>,
    private formBuilder: UntypedFormBuilder,
    public spinner: LoadingSpinnerService,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    public roleService: ImpersonateRolesService,
    private route: ActivatedRoute,
    public copyService: CopyService,
    private replaceVehicleService: ReplaceVehicleService
  ) {}

  ngOnInit() {
    this.pageTitle.set('Replace a Vehicle');
    this.dockingBarService.registerHeading('Replace a Vehicle');
    this.buildForm();
    // Initialize the form
    this.vehicleId = this.route.snapshot.paramMap.get('id');
    // Get the policy number
    // Subscribe to get the advance auto risks
    this.store
      .select(fromRoot.getReplaceVehicleRisks)
      .pipe(takeUntil(this.stop$))
      .subscribe(vehicles => {
        if (vehicles) {
          const matchingVehicle = vehicles.find(vehicle => {
            return _get(vehicle, 'id') === this.vehicleId;
          });
          this.tpi = _filter(matchingVehicle.vehicleRoles, { roleType: 'ThirdPartyInterest' })[0];
          if (this.tpi) {
            this.tpiQuestion.question = `Is ${this.tpi.name} the lender for your new vehicle?`;
          }
          if (this.replaceVehicleService.getAdvancePolicyEligibility(matchingVehicle)) {
            this.policyNumber = matchingVehicle.policyNumber;
            this.replaceVin = matchingVehicle.vin;
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: ['/policies/replace-vehicle/' + this.vehicleId + '/grace-period'],
                extras: { replaceUrl: true }
              })
            );
          }
        }
      });

    this.store
      .select(fromRoot.getReplaceVehiclePremium)
      .pipe(takeUntil(this.stop$))
      .subscribe(premium => {
        if (_has(premium, 'monthlyPremium')) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/policies/replace-vehicle/' + this.vehicleId + '/review'],
              extras: { replaceUrl: true }
            })
          );
        }
      });

    // Listen to vehicle select errors
    this.hasError = this.store.select(vehicleQuery.selectHasError);

    combineLatest(
      this.store.select(fromRoot.getReplaceVehicleHasError),
      this.store.select(fromRoot.getReplaceVehicleStatus),
      this.roleService.isAuthorized('replacevehicle_vinyear'),
      (hasError, status, isAuthorized) => ({
        hasError,
        status,
        isAuthorized
      })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        if (state.hasError && state.status) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/policies/risk-selection/grace-period']
            })
          );
        }
      });
  }

  ngAfterViewInit() {
    this.store.dispatch(
      new replaceVehicleActions.SendAnalyticsAction({
        pageAnalyticName: 'UWAndTPIDetails'
      })
    );
  }
  private buildForm() {
    this.underWritingQuestionsForm = this.formBuilder.group({});
    this.questions.forEach(question =>
      this.underWritingQuestionsForm.addControl(
        'question' + question.id,
        this.formBuilder.control('', Validators.required)
      )
    );
    this.underWritingQuestionsForm.addControl(
      'question' + this.tpiQuestion.id,
      this.formBuilder.control('')
    );
  }

  answersSubmit() {
    this.spinner.start({
      secondaryMessage: this.copyService.getCopy('policy.quoteCalculatingLoadingSpinnerText'),
      blockActions: true
    });
    const answers = this.underWritingQuestionsForm.value;
    const eligibleToReplaceVehicle = answers.question1 === 'no' && answers.question2 === 'no';

    combineLatest(
      this.store.select(vehicleQuery.selectCriteria),
      this.store.select(PolicySelectors.selectVehiclePolicies),
      (vehicleSelectionDetails, policies) => ({ vehicleSelectionDetails, policies })
    )
      .pipe(take(1))
      .subscribe(({ vehicleSelectionDetails, policies }) => {
        const isNewVehicleCoverage = policies
          .find(policy => policy.policyNumber === this.policyNumber)
          .vehicles.find(vehicle => vehicle.vehicleId === this.vehicleId)
          .vehicleCoverages.some(
            coverage => coverage.code === this.getCoverageCodes(coverage.code)
          );

        const isEligibleToQuote =
          (!!isNewVehicleCoverage && Number(vehicleSelectionDetails.odometer) < 500) ||
          !isNewVehicleCoverage;

        if (eligibleToReplaceVehicle && isEligibleToQuote) {
          this.getQuoteRequest();
        } else {
          this.store.dispatch(
            fromVehicleActions.SetCriteria({
              copyTPIIndicator: this.underWritingQuestionsForm.get('question4').value,
              underWritingQuestions: this.getUnderWritingQuestionsForPayload([...this.questions]),
              TPIName: _get(this.tpi, 'name', '')
            })
          );
          // Can't bind online take them to a offine bind page to let them notify their agent.
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/policies/replace-vehicle/' + this.vehicleId + '/offlinebind']
            })
          );
        }
      });
  }

  private getQuoteRequest() {
    this.store
      .pipe(
        select(vehicleQuery.selectNewVehicle),
        withLatestFrom(this.store.select(vehicleQuery.selectCriteria)),
        take(1)
      )
      .subscribe(([newVehicle, selectionDetails]) => {
        this.store.dispatch(
          fromVehicleActions.SetCriteria({
            copyTPIIndicator: this.underWritingQuestionsForm.get('question4').value,
            underWritingQuestions: this.getUnderWritingQuestionsForPayload([...this.questions]),
            TPIName: _get(this.tpi, 'name', '')
          })
        );
        const effectiveDate = new Date();
        effectiveDate.setDate(effectiveDate.getDate() + 1);

        // Fill this in to build the new payload
        const payload: ReplaceVehicleQuoteApiRequest = {
          copyTPIIndicator:
            this.underWritingQuestionsForm.get('question4').value === 'yes' ? true : false,
          underwritingQuestions: this.getUnderWritingQuestionsForPayload(
            [...this.questions.slice(0, 2)],
            true
          ),
          policyNumber: this.policyNumber,
          effectiveDate: effectiveDate,
          replaceVIN: this.replaceVin,
          newVehicle: {
            vin: selectionDetails.vin,
            year: selectionDetails.year,
            odometer: selectionDetails.odometer
          }
        };
        this.store.dispatch(new replaceVehicleActions.ReplaceVehicleGetPremiumAction(payload));
      });
  }

  cancel() {
    // Route to history -1 ??
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies']
      })
    );
  }

  answerSelected(questionObject: UnderwritingQuestionModel) {
    // Check if question 3 is yes or no to display TPI question, validate if shown
    if (questionObject.id === 3) {
      if (questionObject.isAnswerYes && this.tpi) {
        this.underWritingQuestionsForm.get('question4').setValidators([Validators.required]);
        this.showTPIQuestion = true;
      } else {
        this.underWritingQuestionsForm.get('question4').setValidators(null);
        // Removing validators does not automatically mark the field valid, so we trigger manually here
        this.underWritingQuestionsForm.controls['question4'].updateValueAndValidity();
        this.showTPIQuestion = false;
      }
    }
  }

  /**
   * @author: Abhishek Singh
   * @param questions
   * @description: Get the question and answers in the format which is expected by the API.
   */
  private getUnderWritingQuestionsForPayload(
    questions: UnderwritingQuestionModel[],
    mapQuestion?: boolean
  ): UnderwritingQuestion[] {
    const underWritingQuestionsForPayload: UnderwritingQuestion[] = [];
    for (const questionItem of questions) {
      if (questionItem.question) {
        underWritingQuestionsForPayload.push({
          question: mapQuestion ? questionItem.mappedQuestion : questionItem.question,
          answer:
            this.underWritingQuestionsForm.get('question' + questionItem.id).value === 'yes'
              ? 'true'
              : 'false'
        });
      }
    }
    return underWritingQuestionsForPayload;
  }

  /**
   * @param code
   * @description Checks if the code passed in the params matches one of the New car coverage codes.
   */
  private getCoverageCodes(code: string) {
    if (code === 'PANewCarReplaceCov_af' || code === 'PAAgreedValAmtCov_af') {
      return code;
    }
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    this.spinner.stop();
  }
}
